import React from "react";
import { Alert, Button, Modal, Space, Table, Tag, Typography } from "antd";
import {
  ExperimentOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { splitStockShortageItems } from "../utils/stockReplenishment";

const { Text } = Typography;

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    maximumFractionDigits: 5,
  });

const StockShortageActionsModal = ({
  open,
  items = [],
  title = "Stock insuffisant",
  onCancel,
  onPurchase,
  onAdjustment,
  onProduction,
  canPurchase = false,
  canAdjust = false,
  canProduce = false,
  productionTerm = "production",
  productionActionLabel,
}) => {
  const distinguishesProduction = typeof onProduction === "function";
  const { productionItems, purchaseItems } = distinguishesProduction
    ? splitStockShortageItems(items)
    : { productionItems: [], purchaseItems: items };
  const normalizedProductionTerm =
    String(productionTerm || "production").trim().toLowerCase() || "production";
  const productionButtonLabel =
    productionActionLabel ||
    `Lancer la ${normalizedProductionTerm} — recommandé`;
  const hasPurchaseAction = purchaseItems.length > 0 && canPurchase;
  const hasProductionAction = productionItems.length > 0 && canProduce;
  const hasAdjustmentAction = items.length > 0 && canAdjust;
  const hasPartiallyBlockedItems =
    (productionItems.length > 0 && !canProduce && !canAdjust) ||
    (purchaseItems.length > 0 && !canPurchase && !canAdjust);
  const columns = [
    {
      title: "Produit",
      dataIndex: "product_name",
      key: "product_name",
      render: (value, record) => value || `Produit ${record.product_id}`,
    },
    {
      title: "Besoin",
      dataIndex: "required_quantity",
      key: "required_quantity",
      align: "right",
      render: formatQuantity,
    },
    {
      title: "Disponible",
      dataIndex: "available_quantity",
      key: "available_quantity",
      align: "right",
      render: formatQuantity,
    },
    {
      title: "À compléter",
      dataIndex: "missing_quantity",
      key: "missing_quantity",
      align: "right",
      render: (value, record) => (
        <Text strong type="danger">
          {formatQuantity(value)} {record.unit_short_name || record.unit_name || ""}
        </Text>
      ),
    },
    distinguishesProduction
      ? {
          title: "Approvisionnement",
          key: "supply_method",
          render: (_, record) =>
            splitStockShortageItems([record]).productionItems.length > 0 ? (
              <Tag color="purple">
                {normalizedProductionTerm === "cuisine"
                  ? "Cuisine"
                  : "Production"}
              </Tag>
            ) : (
              <Tag color="blue">Achat</Tag>
            ),
        }
      : null,
  ].filter(Boolean);

  const recommendation = (() => {
    if (productionItems.length > 0 && purchaseItems.length > 0) {
      return {
        message: "Deux circuits d’approvisionnement sont nécessaires",
        description: `${productionItems.length} article${
          productionItems.length > 1 ? "s sont fabriqués" : " est fabriqué"
        } ${
          normalizedProductionTerm === "cuisine"
            ? "en cuisine"
            : "par production"
        } et ${purchaseItems.length} article${
          purchaseItems.length > 1 ? "s doivent" : " doit"
        } être approvisionné par achat. Traitez chaque groupe avec son action recommandée.`,
      };
    }

    if (productionItems.length > 0) {
      return {
        message: `${
          normalizedProductionTerm === "cuisine" ? "Cuisine" : "Production"
        } recommandée`,
        description: `Ces articles sont fabriqués en interne. Lancez la ${normalizedProductionTerm} pour créer le stock manquant ; utilisez l’ajustement uniquement pour corriger un écart réel d’inventaire.`,
      };
    }

    return {
      message: (
        <Space wrap>
          <span>Achat recommandé</span>
          <Tag color="blue">Bonne pratique</Tag>
        </Space>
      ),
      description:
        "L’achat conserve l’historique du fournisseur et du coût d’approvisionnement. Utilisez l’ajustement uniquement pour corriger un écart réel d’inventaire.",
    };
  })();

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      width={760}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Continuer plus tard
        </Button>,
        hasAdjustmentAction ? (
          <Button key="adjust" icon={<ToolOutlined />} onClick={onAdjustment}>
            Faire un ajustement
          </Button>
        ) : null,
        hasPurchaseAction ? (
          <Button
            key="purchase"
            type="primary"
            icon={<ShoppingCartOutlined />}
            onClick={onPurchase}
          >
            Créer un achat — recommandé
          </Button>
        ) : null,
        hasProductionAction ? (
          <Button
            key="production"
            type="primary"
            icon={<ExperimentOutlined />}
            onClick={onProduction}
          >
            {productionButtonLabel}
          </Button>
        ) : null,
      ].filter(Boolean)}
    >
      <Alert
        type="info"
        showIcon
        message={recommendation.message}
        description={recommendation.description}
        style={{ marginBottom: 16 }}
      />

      <Table
        size="small"
        rowKey={(record) => String(record.product_id)}
        dataSource={items}
        columns={columns}
        pagination={false}
        scroll={{ x: 620 }}
        locale={{ emptyText: "Aucun produit manquant identifié" }}
      />

      {hasPartiallyBlockedItems &&
        (hasPurchaseAction || hasProductionAction || hasAdjustmentAction) && (
          <Alert
            type="warning"
            showIcon
            message="Certaines lignes nécessitent une permission supplémentaire"
            description="Les actions affichées ne couvrent que les groupes que vous êtes autorisé à traiter. Un utilisateur habilité devra prendre en charge les autres lignes."
            style={{ marginTop: 16 }}
          />
        )}

      {!hasPurchaseAction && !hasProductionAction && !hasAdjustmentAction && (
        <Alert
          type="warning"
          showIcon
          message="Aucune action disponible"
          description={
            productionItems.length > 0
              ? `Vous n’avez pas les permissions nécessaires pour lancer la ${normalizedProductionTerm} ou créer un ajustement de stock.`
              : "Vous n’avez pas les permissions nécessaires pour créer un achat ou un ajustement de stock."
          }
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default StockShortageActionsModal;
